import { useEffect, useState } from 'react';
import type { ConversationListItem } from '@/shared/api';
import { Button, SurfaceStaleHint } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { filterLabel, INBOX_FILTERS, type InboxFilter } from '../inboxModel';
import { ThreadListState } from './ThreadListState';
import { ThreadRow } from './ThreadRow';

export type { InboxFilter } from '../inboxModel';

const PILL = 'rounded-pill flex min-h-11 items-center px-3 text-xs font-bold';

/** Spalte „Unterhaltungen“: Kanal, Liste, „Weitere laden“. Der Umschalter steht im Kopf. */
export function ThreadList({
  threads,
  selectedId,
  onSelect,
  filter,
  onFilter,
  awaitingOnly,
  loading,
  paused = false,
  error,
  onRetry,
  stale = false,
  hasMore,
  loadingMore,
  onLoadMore,
}: {
  threads: ConversationListItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  filter: InboxFilter;
  onFilter: (filter: InboxFilter) => void;
  awaitingOnly: boolean;
  loading?: boolean;
  /** Ohne Verbindung noch nichts geladen — weder „lädt“ noch „leer“. */
  paused?: boolean;
  error?: unknown;
  onRetry?: () => void;
  /** Nachladen gescheitert, die Liste zeigt den letzten Stand. */
  stale?: boolean;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}) {
  const now = useMinuteClock();
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        role="group"
        aria-label="Kanal"
        className="border-hairline flex flex-none flex-wrap gap-1.5 border-b p-3"
      >
        {INBOX_FILTERS.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={option === filter}
            onClick={() => onFilter(option)}
            className={cn(
              PILL,
              option === filter
                ? 'bg-ink text-white'
                : 'border-line text-ink hover:bg-card-alt border bg-white',
            )}
          >
            {filterLabel(option)}
          </button>
        ))}
      </div>
      {stale && onRetry && <SurfaceStaleHint onRetry={onRetry} retrying={false} />}
      <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
        {loading || paused || error || threads.length === 0 ? (
          <ThreadListState
            loading={loading}
            paused={paused}
            error={error}
            onRetry={onRetry}
            awaitingOnly={awaitingOnly}
            filtered={filter !== 'Alle'}
          />
        ) : (
          <ul className="flex flex-col gap-0.5">
            {threads.map((thread) => (
              <li key={thread.id}>
                <ThreadRow
                  thread={thread}
                  selected={thread.id === selectedId}
                  now={now}
                  onSelect={() => onSelect(thread.id)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
      {(hasMore || awaitingOnly) && !loading && !paused && !error && (
        <div className="border-hairline flex flex-none flex-col gap-2 border-t p-3">
          {hasMore && (
            <Button
              type="button"
              variant="secondary"
              className="min-h-11"
              loading={loadingMore}
              onClick={onLoadMore}
            >
              Weitere laden
            </Button>
          )}
          {awaitingOnly && (
            <p className="text-ink-soft text-xs leading-relaxed">
              Antwortest du direkt in Airbnb oder Booking.com, verschwindet die Unterhaltung hier,
              sobald der Kanal die Nachricht liefert.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** „wartet seit …“ bleibt ohne Neuladen richtig; eine Minute genügt. */
function useMinuteClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}
